import {useSiteLanguage} from "../../career/SiteLanguage";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ArrowLeft,
  BriefcaseBusiness,
  CalendarDays,
  ChevronRight,
  FileText,
  Mail,
  MapPin,
  Phone,
  Store,
  UserRound,
  UsersRound,
  X,
} from "lucide-react";
import "../../styles/mudur/MagazaIlanlarim.css";
import { collectionValues, resolveMediaUrl } from "../../utils/mediaUrl";

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

const tarihYaz = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("tr-TR");
};

const metin = (value) => (value === null || value === undefined || value === "" ? "-" : value);

const hataMesaji = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.title || fallback;

const MagazaIlanlarim = () => {
  const {tx} = useSiteLanguage();
  const token = localStorage.getItem("token");
  const authHeaders = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );
  const [ilanlar, setIlanlar] = useState([]);
  const [seciliIlan, setSeciliIlan] = useState(null);
  const [basvurular, setBasvurular] = useState([]);
  const [cv, setCv] = useState(null);
  const [cvFoto, setCvFoto] = useState("");
  const [loading, setLoading] = useState(true);
  const [basvuruLoading, setBasvuruLoading] = useState(false);
  const [cvLoading, setCvLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    const getir = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axios.get(`${apiBaseUrl}/api/mudur/ilanlar`, authHeaders);
        if (!mounted) return;
        setIlanlar(collectionValues(response.data?.data));
      } catch (requestError) {
        if (mounted) setError(hataMesaji(requestError, "Paylaşılan ilanlar alınamadı."));
      } finally {
        if (mounted) setLoading(false);
      }
    };
    getir();
    return () => {
      mounted = false;
    };
  }, [authHeaders]);

  useEffect(() => () => {
    if (cvFoto) URL.revokeObjectURL(cvFoto);
  }, [cvFoto]);

  const basvurulariAc = async (ilan) => {
    setSeciliIlan(ilan);
    setBasvurular([]);
    setBasvuruLoading(true);
    setError("");
    try {
      const response = await axios.get(
        `${apiBaseUrl}/api/mudur/ilanlar/${ilan.ilanId}/basvurular`,
        authHeaders
      );
      setBasvurular(collectionValues(response.data?.data));
    } catch (requestError) {
      setError(hataMesaji(requestError, "İlana ait başvurular alınamadı."));
    } finally {
      setBasvuruLoading(false);
    }
  };

  const cvAc = async (basvuru) => {
    setCvLoading(true);
    setCv(null);
    if (cvFoto) {
      URL.revokeObjectURL(cvFoto);
      setCvFoto("");
    }
    try {
      const [cvResponse, photoResponse] = await Promise.all([
        axios.get(
          `${apiBaseUrl}/api/mudur/ilanlar/${seciliIlan.ilanId}/basvurular/${basvuru.basvuruId}/cv`,
          authHeaders
        ),
        axios.get(
          `${apiBaseUrl}${basvuru.profilFotoEndpoint}`,
          { ...authHeaders, responseType: "blob", validateStatus: status => status === 200 || status === 404 }
        ),
      ]);
      setCv({ ...cvResponse.data?.data, basvuru });
      if (photoResponse.status === 200 && photoResponse.data?.size) {
        setCvFoto(URL.createObjectURL(photoResponse.data));
      }
    } catch (requestError) {
      setError(hataMesaji(requestError, "Adayın CV bilgileri alınamadı."));
    } finally {
      setCvLoading(false);
    }
  };

  const cvKapat = () => {
    setCv(null);
    if (cvFoto) URL.revokeObjectURL(cvFoto);
    setCvFoto("");
  };

  if (loading) {
    return <div className="mi-state-card">{tx("Paylaşılan ilanlar yükleniyor...")}</div>;
  }

  return (
    <div className="magaza-ilan-page">
      <header className="mi-page-header">
        <div>
          <span>{tx("PAYLAŞILAN İLANLAR")}</span>
          <h1>{tx("İlanlarım")}</h1>
          <p>{tx("Paylaşılan ilanları, başvuran adayları ve adayların özgeçmişlerini görüntüleyin.")}</p>
        </div>
        <div className="mi-total-badge">
          <BriefcaseBusiness size={20} />
          <strong>{ilanlar.length}</strong>
          <span>{tx("ilan")}</span>
        </div>
      </header>

      {error && <div className="mi-error">{error}</div>}

      {!seciliIlan ? (
        ilanlar.length === 0 ? (
          <div className="mi-state-card">
            <BriefcaseBusiness size={38} />
            <strong>{tx("Paylaşılan ilan bulunmuyor")}</strong>
            <span>{tx("İK tarafından sizinle paylaşılan ilanlar burada görünecek.")}</span>
          </div>
        ) : (
          <div className="mi-job-grid">
            {ilanlar.map((ilan) => {
              return (
                <article className="mi-job-card" key={ilan.ilanId}>
                  <div className="mi-job-cover">
                    {ilan.ilanFotoUrl ? (
                      <img src={resolveMediaUrl(ilan.ilanFotoUrl)} alt="" />
                    ) : (
                      <span>FULLA</span>
                    )}
                    <em className={ilan.status ? "is-active" : "is-passive"}>
                      {ilan.status ? "Yayında" : "Pasif"}
                    </em>
                  </div>
                  <div className="mi-job-content">
                    <small>{ilan.pozisyonAdi || "Açık pozisyon"}</small>
                    <h2>{ilan.ilanBaslik}</h2>
                    <p>{ilan.kisaAciklamasi || "İlan açıklaması bulunmuyor."}</p>
                    <div className="mi-job-meta">
                      <span><Store size={15} /> {ilan.magazaAdi}</span>
                      <span><CalendarDays size={15} /> {tarihYaz(ilan.gecerlilikTarihi)}</span>
                    </div>
                    {ilan.paylasilanKisiSayisi > 1 && (
                      <div className="mi-shared-stores">{tx("Sizinle birlikte")}{ilan.paylasilanKisiSayisi}{tx("kişiyle paylaşılıyor")}</div>
                    )}
                    <button type="button" onClick={() => basvurulariAc(ilan)}>
                      <UsersRound size={17} />{tx("Başvuruları gör")}<b>{ilan.basvuruSayisi || 0}</b>
                      <ChevronRight size={17} />
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )
      ) : (
        <section className="mi-applications-view">
          <button type="button" className="mi-back-button" onClick={() => setSeciliIlan(null)}>
            <ArrowLeft size={17} />{tx("İlanlara dön")}</button>

          <div className="mi-selected-job">
            <div>
              <span>{seciliIlan.pozisyonAdi}</span>
              <h2>{seciliIlan.ilanBaslik}</h2>
              <p><Store size={15} /> {seciliIlan.magazaAdi}</p>
            </div>
            <div><strong>{basvurular.length}</strong><span>{tx("başvuru")}</span></div>
          </div>

          {basvuruLoading ? (
            <div className="mi-state-card">{tx("Başvurular yükleniyor...")}</div>
          ) : basvurular.length === 0 ? (
            <div className="mi-state-card">
              <UsersRound size={38} />
              <strong>{tx("Henüz başvuru yok")}</strong>
              <span>{tx("Bu ilana yapılan yeni başvurular burada listelenecek.")}</span>
            </div>
          ) : (
            <div className="mi-candidate-list">
              {basvurular.map((candidate) => (
                <article className="mi-candidate-row" key={candidate.basvuruId}>
                  <div className="mi-candidate-avatar">
                    {(candidate.adSoyad || "A").split(" ").slice(0, 2).map(part => part[0]).join("")}
                  </div>
                  <div className="mi-candidate-main">
                    <h3>{candidate.adSoyad || "İsimsiz aday"}</h3>
                    <div>
                      <span><Mail size={14} /> {metin(candidate.email)}</span>
                      <span><Phone size={14} /> {metin(candidate.telefon)}</span>
                      <span><MapPin size={14} /> {[candidate.ilce, candidate.il].filter(Boolean).join(" / ") || "-"}</span>
                    </div>
                    {candidate.basvuranAciklama && <p>{candidate.basvuranAciklama}</p>}
                    {candidate.toplamBasvuruSayisi > 1 && (
                      <div className="mi-other-applications">
                        <strong>{candidate.toplamBasvuruSayisi}{tx("başvuru:")}</strong>
                        {collectionValues(candidate.basvurduguIlanlar).map(application => (
                          <span key={`${candidate.basvuruId}-${application.ilanId}`}>
                            {application.ilanBaslik} ({application.magazaAdi})
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="mi-candidate-actions">
                    <small>{tarihYaz(candidate.basvuruTarihi)}</small>
                    <button type="button" onClick={() => cvAc(candidate)} disabled={cvLoading}>
                      <FileText size={16} />{tx("CV görüntüle")}</button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      )}

      {cvLoading && <div className="mi-cv-loading">{tx("CV yükleniyor...")}</div>}

      {cv && (
        <div className="mi-cv-modal" role="dialog" aria-modal="true" aria-label={tx("Aday özgeçmişi")}>
          <div className="mi-cv-backdrop" onClick={cvKapat}></div>
          <div className="mi-cv-dialog">
            <button type="button" className="mi-cv-close" onClick={cvKapat} aria-label={tx("Kapat")}>
              <X size={21} />
            </button>
            <CvIcerigi cv={cv} photoUrl={cvFoto} />
          </div>
        </div>
      )}
    </div>
  );
};

export const CvIcerigi = ({ cv, photoUrl }) => {
  const {tx} = useSiteLanguage();
  const general = cv.genelBilgilerModelDto || {};
  const experiences = collectionValues(cv.deneyimModelDto);
  const educations = collectionValues(cv.egitimModelDto).filter(item => {
    const meaningfulValues = [
      item.egitimLisansAdi,
      item.egitimLise,
      item.yabanciOkul,
      item.egitimKategoriAdi,
      item.egitimBolumAdi,
      item.egitimLiseBolum,
      item.yabanciBolum,
      item.egitimTuruAdi,
    ];
    return meaningfulValues.some(value => value && value !== "-");
  });
  const languages = collectionValues(cv.dilKullaniciModelDto);
  const certificates = collectionValues(cv.sertifikaModelDto);
  const fullName = `${general.adi || ""} ${general.soyadi || ""}`.trim() || cv.basvuru?.adSoyad;

  return (
    <div className="mi-cv-document">
      <header className="mi-cv-profile">
        <div className="mi-cv-photo">
          {photoUrl ? <img src={photoUrl} alt={fullName} /> : <UserRound size={42} />}
        </div>
        <div>
          <span>{tx("ADAY ÖZGEÇMİŞİ")}</span>
          <h2>{fullName}</h2>
          <p>{[general.kullaniciIlcelerAdi, general.kullaniciIlAdi].filter(Boolean).join(" / ")}</p>
        </div>
      </header>

      <section className="mi-cv-section">
        <h3>{tx("İletişim ve kişisel bilgiler")}</h3>
        <div className="mi-cv-info-grid">
          <CvInfo label="E-posta" value={general.email} />
          <CvInfo label="Telefon" value={general.telefon} />
          <CvInfo label="Doğum tarihi" value={tarihYaz(general.dogumTarihi)} />
          <CvInfo label="Doğum yeri" value={general.dogumYeri} />
          <CvInfo label="Adres" value={general.adres} />
          <CvInfo label="Ehliyet" value={general.ehliyet} />
          <CvInfo label="Askerlik" value={general.askerlikDurumu} />
          <CvInfo label="Seyahat engeli" value={general.seyahatDurumu === true ? "Yok" : general.seyahatDurumu === false ? "Var" : "-"} />
        </div>
      </section>

      <CvListeBolumu title={tx("İş deneyimi")} empty="İş deneyimi eklenmemiş." items={experiences} renderItem={(item) => (
        <div className="mi-cv-timeline-item" key={item.deneyimID}>
          <strong>{item.deneyimPozisyon || "Pozisyon"}</strong>
          <span>{item.sirketAdi || "Şirket belirtilmemiş"}</span>
          <small>{tarihYaz(item.baslangicTarihi)} – {item.suanCalisiyorMu ? "Devam ediyor" : tarihYaz(item.bitisTarihi)}</small>
          {item.aciklama && <p>{item.aciklama}</p>}
        </div>
      )} />

      <CvListeBolumu title={tx("Eğitim")} empty="Eğitim bilgisi eklenmemiş." items={educations} renderItem={(item) => (
        <div className="mi-cv-timeline-item" key={item.egitimID}>
          <strong>{item.egitimLisansAdi || item.egitimLise || item.yabanciOkul || item.egitimKategoriAdi || "Okul"}</strong>
          <span>{item.egitimBolumAdi || item.egitimLiseBolum || item.yabanciBolum || item.egitimTuruAdi || "-"}</span>
          <small>{item.egitimDurumuAdi || tarihYaz(item.bitisTarihi)}</small>
        </div>
      )} />

      <CvListeBolumu title={tx("Yabancı dil")} empty="Yabancı dil bilgisi eklenmemiş." items={languages} renderItem={(item) => (
        <div className="mi-cv-inline-item" key={item.dilKullaniciId}>
          <strong>{item.dilAdi || "Dil"}</strong><span>{item.seviyeAdi || "Seviye belirtilmemiş"}</span>
        </div>
      )} />

      <CvListeBolumu title={tx("Sertifikalar")} empty="Sertifika eklenmemiş." items={certificates} renderItem={(item) => (
        <div className="mi-cv-inline-item" key={item.sertifikaID}>
          <strong>{item.sertifikaAdi || "Sertifika"}</strong><span>{item.sertifikaAciklama || ""}</span>
        </div>
      )} />
    </div>
  );
};

const CvInfo = ({ label, value }) => (
  <div><span>{label}</span><strong>{metin(value)}</strong></div>
);

const CvListeBolumu = ({ title, items, empty, renderItem }) => (
  <section className="mi-cv-section">
    <h3>{title}</h3>
    {items.length ? <div className="mi-cv-list">{items.map(renderItem)}</div> : <p className="mi-cv-empty">{empty}</p>}
  </section>
);

export default MagazaIlanlarim;
